/**
 * The grid's Rep filter (1st Phorm, 10/29: "a rep sees their own forecast").
 *
 * A forecast customer's reps are the `sales_rep` of the brand's customer records named like it —
 * the full name, or the account part of `Retailer @ Distributor` — by name or alternative name.
 * The same rule the server enforces on save (`forecast::forecast_customer_owners`), so what the
 * filter calls "mine" is what a rep may change.
 */

export interface RepCustomerRecord {
  name: string
  sales_rep?: string | null
  other_remote_id?: string | null
}

/** `All reps`, or one rep's name. */
export const ALL_REPS = "__all__"

function normalize(name: string): string {
  return name.trim().toLowerCase()
}

function record_names(record: RepCustomerRecord): string[] {
  const alternatives = (record.other_remote_id ?? "")
    .split("|")
    .map((name: string) => name.trim())
    .filter((name: string) => name.length > 0 && !/^\d+$/.test(name))
  return [record.name, ...alternatives].map(normalize)
}

function split_reps(raw: string | null | undefined): string[] {
  return (raw ?? "")
    .split("|")
    .map((rep: string) => rep.trim())
    .filter((rep: string) => rep.length > 0)
}

export function forecast_customer_owners(forecast_customer: string, records: RepCustomerRecord[]): string[] {
  const full = normalize(forecast_customer)
  const account = full.split(" @ ")[0].trim()
  const owners = new Set<string>()
  for (const record of records) {
    const names = record_names(record)
    if (names.includes(full) || names.includes(account)) {
      for (const rep of split_reps(record.sales_rep)) owners.add(rep)
    }
  }
  return [...owners].sort()
}

/** Every rep named on a customer record, A–Z. */
export function reps_in_records(records: RepCustomerRecord[]): string[] {
  const reps = new Set<string>()
  for (const record of records) for (const rep of split_reps(record.sales_rep)) reps.add(rep)
  return [...reps].sort((a: string, b: string) => a.localeCompare(b))
}

/** The signed-in user's name as a `sales_rep` would spell it, or `null` without one. */
export function user_rep_name(user: { first_name?: string | null; last_name?: string | null } | null | undefined): string | null {
  const name = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim()
  return name.length > 0 ? name : null
}

interface RepNodeLike {
  name: string
  level?: string
  children?: RepNodeLike[]
}

/**
 * The tree with only `rep`'s customers (and whatever sits above them). Rows below a kept customer
 * come along whole. `ALL_REPS` returns the tree as it is.
 */
export function filter_tree_by_rep<T extends RepNodeLike>(nodes: T[], rep: string, records: RepCustomerRecord[]): T[] {
  if (rep === ALL_REPS) return nodes
  const wanted = normalize(rep)
  function keep(node: T): T | null {
    if (String(node.level) === "customer") {
      return forecast_customer_owners(node.name, records).some((owner: string) => normalize(owner) === wanted) ? node : null
    }
    const children = Array.isArray(node.children) ? node.children : []
    if (children.length === 0) return null
    const kept = children.map((child: RepNodeLike) => keep(child as T)).filter((child: T | null): child is T => child != null)
    return kept.length > 0 ? ({ ...node, children: kept } as T) : null
  }
  return nodes.map(keep).filter((node: T | null): node is T => node != null)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  const records: RepCustomerRecord[] = [
    { name: "Walmart", sales_rep: "Alisse Koishor" },
    { name: "Target", sales_rep: "Megan Wade", other_remote_id: "Target Corp" },
    { name: "Casey's", sales_rep: "Trevor Gibbs|Alisse Koishor" },
    { name: "KeHE", sales_rep: null },
  ]

  describe("rep filter", () => {
    it("reads a forecast customer's reps the way the server does", () => {
      expect(forecast_customer_owners("Walmart @ Direct", records)).toEqual(["Alisse Koishor"])
      expect(forecast_customer_owners("Casey's @ Direct", records)).toEqual(["Alisse Koishor", "Trevor Gibbs"])
      expect(forecast_customer_owners("KeHE", records)).toEqual([])
      expect(reps_in_records(records)).toEqual(["Alisse Koishor", "Megan Wade", "Trevor Gibbs"])
      expect(user_rep_name({ first_name: "Alisse", last_name: "Koishor" })).toBe("Alisse Koishor")
      expect(user_rep_name({ first_name: null, last_name: null })).toBeNull()
    })

    it("keeps only one rep's customers, with what sits above and below them", () => {
      const tree = [
        { name: "Walmart @ Direct", level: "customer", children: [{ name: "Sticks", level: "sku" }] },
        { name: "Target @ Direct", level: "customer", children: [] },
        { name: "Mass", level: "class_of_trade", children: [{ name: "Casey's @ Direct", level: "customer" }, { name: "KeHE", level: "customer" }] },
      ]
      const mine = filter_tree_by_rep(tree, "alisse koishor", records)
      expect(mine.map((n) => n.name)).toEqual(["Walmart @ Direct", "Mass"])
      expect(mine[0].children?.[0].name).toBe("Sticks")
      expect(mine[1].children?.map((n) => n.name)).toEqual(["Casey's @ Direct"])
      expect(filter_tree_by_rep(tree, ALL_REPS, records)).toBe(tree)
    })
  })
}
